import React from 'react';
import PageHero from '../components/PageHero';
import { Helmet } from "react-helmet-async";
import CTASection from '../components/CTASection';
import doctor from '../assets/Doctor.webp';
import { HERO_BG, ABOUT_GALLERY_1, ABOUT_GALLERY_2 } from '../assets/images';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Updated icons to match the sleep/airway theme
const { FiAward, FiMoon, FiShield, FiWind, FiActivity, FiUsers } = FiIcons;

export default function About() {
  const expertise = [
    {
      icon: FiMoon,
      title: "Sleep Apnea (Adults)",
      desc: "Custom oral appliance therapy to treat Obstructive Sleep Apnea, eliminate snoring, and restore restful sleep without CPAP."
    },
    {
      icon: FiUsers,
      title: "Pediatric Sleep & Airway",
      desc: "Early intervention strategies to guide jaw growth, expand airways, and resolve sleep-disordered breathing in growing children."
    },
    {
      icon: FiWind,
      title: "Myofunctional Therapy",
      desc: "Neuromuscular re-education of oral and facial muscles to promote proper nasal breathing, swallowing, and resting posture."
    },
    {
      icon: FiActivity,
      title: "TMJ Disorder Treatment",
      desc: "Comprehensive diagnostics and non-invasive therapies to alleviate chronic jaw pain, clicking, and associated headaches."
    }
  ];

  return (
    <div className="bg-gray-50/50">

      <Helmet>
        <title>About Dental Sleep Care</title>
        <meta name="description" content="Airway-focused dental care for sleep apnea and TMJ treatment." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalOrganization",
            "name": "Dental Sleep Care"
          })}
        </script>
      </Helmet>

      <PageHero
        title="About Dental Sleep Care"
        subtitle="Pioneering comprehensive solutions for Sleep Apnea, TMJ Disorders, and Airway Health for patients of all ages."
        image={HERO_BG}
      />

      {/* Doctor Profile Section */}
      <section className="py-24 px-6 bg-white relative overflow-hidden">
        {/* Decorative background blob */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 rounded-l-full -mr-32 blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

            {/* Image Side */}
            <div className="lg:col-span-5 order-1 relative">
              <div className="relative z-10">
                <img
                  src={doctor}
                  alt="Dr. Kranthi Kumar Reddy"
                  className="rounded-2xl w-full shadow-2xl object-cover h-[500px] lg:h-[650px] border-4 border-white"
                />
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      <SafeIcon icon={FiAward} size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-secondary text-sm">Distinguished Alumnus</p>
                      <p className="text-xs text-gray-500">Dr. NTR University</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text Side */}
            <div className="lg:col-span-7 order-2">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
                Meet Our Founder
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-2">Dr. Kranthi Kumar Reddy</h2>
              <p className="text-primary font-medium text-xl mb-8">Specialist in Airway, Sleep & Pediatric Dentistry</p>

              <div className="space-y-5 text-lg text-gray-600 leading-relaxed mb-8">
                <p>
                  Our founder, Dr. Kranthi Kumar Reddy, is a celebrated name in Pediatric Dentistry, having obtained his graduation and masters from the esteemed Dr. NTR University of Health Sciences.
                </p>
                <p>
                  Driven by a profound understanding of children's needs and a passion for creating a positive impact, Dr. Reddy established the first-of-its-kind pediatric dental center in our state — <span className="font-semibold text-secondary">Children's Dentistry</span>.
                </p>
                <p>
                  Recognizing the critical link between early facial growth, airway development, and lifelong sleep quality, Dr. Reddy expanded his focus. Today, at <span className="font-semibold text-secondary">Dental Sleep Care</span>, he utilizes his profound expertise in craniofacial development to treat the root causes of Sleep Apnea and TMJ Disorders in both children and adults, ensuring whole-body health through optimal breathing and sleep.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 border-t border-gray-100 pt-8">
                <div className="flex items-center gap-4 text-secondary">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-primary shrink-0 shadow-sm">
                    <SafeIcon icon={FiWind} size={22} />
                  </div>
                  <span className="font-medium">Airway-Focused Approach</span>
                </div>
                <div className="flex items-center gap-4 text-secondary">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-primary shrink-0 shadow-sm">
                    <SafeIcon icon={FiMoon} size={22} />
                  </div>
                  <span className="font-medium">Advanced Sleep Solutions</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="py-24 px-6 bg-secondary relative overflow-hidden">
        {/* Subtle Background Glow for Depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent"></div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          {/* Stylized Icon Badge */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mb-8 shadow-sm">
            <SafeIcon icon={FiShield} className="text-4xl text-primary" />
          </div>

          {/* Emphasized Heading */}
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 tracking-tight drop-shadow-sm">
            Our Treatment Philosophy
          </h2>

          {/* Accent Divider Line */}
          <div className="w-24 h-1.5 bg-primary mx-auto mb-10 rounded-full"></div>

          {/* Highly Readable, Impactful Text */}
          <blockquote className="text-xl md:text-2xl text-white leading-relaxed font-medium relative">
            <span className="text-primary/40 text-6xl absolute -top-6 -left-8 font-serif leading-none">"</span>
            We believe that restful sleep and pain-free living are fundamental rights. By looking beyond the teeth to address the root causes of sleep apnea, compromised airways, and TMJ dysfunction, we provide non-invasive, structural, and functional therapies that help our patients reclaim their health, vitality, and quality of life.
            <span className="text-primary/40 text-6xl absolute -bottom-8 -right-8 font-serif leading-none">"</span>
          </blockquote>
        </div>
      </section>

      {/* Specialized Expertise Cards */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">Dedicated Areas of Expertise</h2>
            <p className="text-gray-600 text-lg">Comprehensive diagnostic and therapeutic solutions tailored for optimal airway health and restorative sleep.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-primary transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={item.icon} size={28} />
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-24 px-6 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">Inside Our State-of-the-Art Clinic</h2>
            <p className="text-gray-600 text-lg">Designed for comfort, equipped for precision airway diagnostics.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="group overflow-hidden rounded-2xl shadow-md">
              <img src={ABOUT_GALLERY_1} alt="Clinic Interior" className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="group overflow-hidden rounded-2xl shadow-md">
              <img src={ABOUT_GALLERY_2} alt="Treatment Room" className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-500" />
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}